/// <amd-module name="Repos/Views/Import/Components/GettingStarted/GettingStarted" />
import * as React from "react";
import { GitImportRequest, GitRepository } from "Repos/Common/Contracts/Git";
import { IProjectRepositoryInformationService } from "Repos/Common/ProjectRepositoryInformationService";
import { RepoProtocol, RepositoryType } from "Repos/Common/Types";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { ReposMessages } from "Repos/Messages/Components/ReposMessages";
import { initializeRepository } from "Repos/Views/Import/Api";
import { CloneNotice, CloneWithApplication, CloneWithCredential } from "Repos/Views/Import/Clone";
import { PanelImport } from "Repos/Views/Import/PanelImport";
import { Push } from "Repos/Views/Import/Push";
import * as Resources from "Repos/Views/Import/Resources";
import { getGitClient } from "Repos/Views/Import/RestClient/Git";
import { getTFSProject } from "Tfs/Platform/Page";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Checkbox } from "VSSUI/Checkbox";
import { Dropdown, DropdownExpandableButton } from "VSSUI/Dropdown";
import { Header, TitleSize } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { IListSelection, ListSelection } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { noop } from "VSSUI/Util";
import { GettingStartedProps } from "./GettingStarted.Props";

export class GettingStarted extends VssComponent<GettingStartedProps> {
    private defaultGitBranchName: string | undefined;
    private initializeItems: ObservableArray<IListBoxItem>;
    private initializeSelection: ListSelection;
    private messageInitializeFailure = new ObservableValue<React.ReactNode | undefined>(undefined);
    private projectId: string;
    private projectName: string;
    private protocol: ObservableValue<RepoProtocol>;
    private readme = new ObservableValue<boolean>(true);
    private repository: GitRepository;
    private showImport = new ObservableValue<boolean>(false);

    constructor(props: GettingStartedProps, context: IVssComponentContext) {
        super(props, context);

        const vcViewModelDataProviderService = context.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        const vcViewModel = vcViewModelDataProviderService.getViewModel();

        // Save the repository for the current page.
        if (!vcViewModel.gitRepository) {
            throw new Error(""); // @TODO: What do we do if there is no repository available?
        }

        this.repository = vcViewModel.gitRepository;

        const projectInfoService = this.context.pageContext.getService<IProjectRepositoryInformationService>("IProjectRepositoryInformationService");
        const projectInfo = projectInfoService.getData();
        this.defaultGitBranchName = projectInfo?.defaultGitBranchName;

        // If https is available we will use this protocol by default.
        this.protocol = new ObservableValue<RepoProtocol>(vcViewModel.cloneUrl ? RepoProtocol.https : RepoProtocol.ssh);

        // Setup the items for the initialization dropdown.
        this.initializeItems = new ObservableArray<IListBoxItem>([{ id: "none", text: Resources.None }]);
        this.initializeSelection = new ListSelection({
            selectedRanges: [{ beginIndex: 0, endIndex: 0 }],
            selectOnFocus: false
        });

        // Get the project name we display in the title.
        const project = getTFSProject(context.pageContext);
        if (project) {
            this.projectId = project.id;
            this.projectName = project.name;

            // Load the set of additional items asynchronously.
            if (userHasClaim(this.context.pageContext, UserClaims.Member)) {
                const gitClient = getGitClient(context.pageContext);

                gitClient.getTemplateList(project.name, "gitignore").then(templates => {
                    this.initializeItems.push(
                        ...templates.map(template => {
                            return { id: template.name, text: template.name.split(".", 1)[0] };
                        })
                    );
                });
            }
        }
    }

    public render() {
        const cloneTitleId = "clone-title";
        const pushTitleId = "push-title";
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="repo-getting-started-page flex-grow custom-scrollbar scroll-auto-hide">
                    <ReposMessages />
                    <Header title={format(Resources.GettingStartedTitle, this.repository.name)} titleSize={TitleSize.Large} />
                    <div className="page-content page-content-top rhythm-vertical-16">
                        <Card titleProps={{ className: "font-weight-semibold", text: Resources.CloneToComputer, id: cloneTitleId }}>
                            <div className="rhythm-vertical-16 margin-vertical-4">
                                <div className="flex-row flex-start">
                                    <CloneWithCredential className="fixed-width-element" protocol={this.protocol} ariaLabelledBy={cloneTitleId} />
                                    <div className="flex-row flex-center flex-noshrink">
                                        <CloneWithApplication
                                            label={Resources.Or}
                                            labelClassName="body-xl font-weight-semibold margin-horizontal-16"
                                        />
                                    </div>
                                </div>
                                <CloneNotice />
                            </div>
                        </Card>
                        <Card titleProps={{ className: "font-weight-semibold", text: Resources.PushFromCommandLine, id: pushTitleId }}>
                            <Push className="fixed-width-element" protocol={this.protocol} ariaLabelledBy={pushTitleId} />
                        </Card>
                        {
                            userHasClaim(this.context.pageContext, UserClaims.Member) && (
                                <>
                                    <Card titleProps={{ className: "font-weight-semibold", text: Resources.ImportRepository }}>
                                        <Observer showImport={this.showImport}>
                                            {(props: { showImport: boolean }) => (
                                                <div className="margin-vertical-4">
                                                    <Button text={Resources.Import} onClick={() => (this.showImport.value = true)} />
                                                    {props.showImport ? (
                                                        <PanelImport
                                                            onDismiss={() => (this.showImport.value = false)}
                                                            onImport={this.onImport}
                                                            projectId={this.projectId}
                                                            projectName={this.projectName}
                                                            repositoryName={this.repository.name}
                                                        />
                                                    ) : null}
                                                </div>
                                            )}
                                        </Observer>
                                    </Card>
                                    <Card
                                        className="bolt-card-with-header"
                                        renderHeader={() => (
                                            <Header
                                                className="font-weight-semibold"
                                                title={
                                                    <FormatComponent format={Resources.InitializeRepository}>
                                                        <span className="flex-self-center monospaced-text">
                                                            <Icon iconName="OpenSource" />
                                                            {this.defaultGitBranchName}
                                                        </span>
                                                    </FormatComponent>
                                                }
                                            />
                                        )}
                                    >

                                        <Observer
                                            failureMessageInitialize={this.messageInitializeFailure}
                                            initializeSelection={this.initializeSelection}
                                            readme={this.readme}
                                        >
                                            {(props: { failureMessageInitialize?: React.ReactNode; readme: boolean }) => {
                                                return props.failureMessageInitialize ? (
                                                    <MessageCard
                                                        onDismiss={() => (this.messageInitializeFailure.value = undefined)}
                                                        severity={MessageCardSeverity.Error}
                                                    >
                                                        <div className="flex-column body-s">{props.failureMessageInitialize}</div>
                                                    </MessageCard>
                                                ) : (
                                                        <div className="flex-row rhythm-horizontal-16 margin-vertical-4">
                                                            <Checkbox
                                                                checked={props.readme}
                                                                label={Resources.AddReadMe}
                                                                onChange={(event, checked) => (this.readme.value = checked)}
                                                            />
                                                            <Dropdown
                                                                items={this.initializeItems}
                                                                renderExpandable={props => (
                                                                    <DropdownExpandableButton
                                                                        {...props}
                                                                        renderSelectedItems={(selection: IListSelection, items: IListBoxItem[]) => {
                                                                            const item = items[selection.value[0].beginIndex];

                                                                            return (
                                                                                <>
                                                                                    <span className="secondary-text">
                                                                                        {format(Resources.AddAnItem, ".gitignore")}
                                                                                    </span>
                                                                                    {item.text}
                                                                                </>
                                                                            );
                                                                        }}
                                                                    />
                                                                )}
                                                                selection={this.initializeSelection}
                                                            />
                                                            <Button
                                                                className="initialize-button"
                                                                disabled={!props.readme && this.initializeSelection.value[0].beginIndex === 0}
                                                                onClick={this.onInitialize}
                                                                text={Resources.Initialize}
                                                            />
                                                        </div>
                                                    );
                                            }}
                                        </Observer>
                                    </Card>
                                </>
                            )}
                    </div>
                </Page>
            </Surface>
        );
    }

    private onImport = (importOperation: Promise<GitImportRequest>): void => {
        importOperation.then(importRequest => {
            this.props.importRequest.value = importRequest;
            this.showImport.value = false;
        }, noop);
    };

    private onInitialize = () => {
        const selectedIndex = this.initializeSelection.value[0].beginIndex;
        const selectedItem = this.initializeItems.value[selectedIndex];

        initializeRepository(this.context.pageContext, {
            application: selectedItem.id !== "none" ? { id: selectedItem.id, name: selectedItem.text! } : undefined,
            createRepository: false,
            defaultGitBranchName: this.defaultGitBranchName,
            projectId: this.projectId,
            projectName: this.projectName,
            readme: this.readme.value,
            repository: this.repository,
            repositoryName: this.repository.name,
            type: RepositoryType.git
        })
            .then(() => FastPageSwitch(this.context.pageContext, window.location.href, false))
            .catch((error: any) => (this.messageInitializeFailure.value = error));
    };
}
